import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingDown, TrendingUp, RefreshCw, Search,
  ArrowUpRight, ArrowDownRight, Activity,
  BarChart2, DollarSign, Target, Award,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';
const P  = '#C29500';
const P2 = '#E8C040';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

function TypeBadge({ type, isBuy }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
      background: isBuy ? `${G}14` : `${R}14`,
      color: isBuy ? G : R,
    }}>
      {isBuy ? <ArrowUpRight size={11}/> : <ArrowDownRight size={11}/>}
      {isBuy ? 'BUY' : 'SELL'}
    </span>
  );
}

export default function TradeHistory() {
  const [trades,      setTrades]      = useState([]);
  const [positions,   setPositions]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('open');
  const [search,      setSearch]      = useState('');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [stats,       setStats]       = useState({ total_trades:0, win_trades:0, loss_trades:0, total_profit:0, win_rate:0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [posRes, histRes] = await Promise.all([
        api.get('/trading/positions').catch(() => ({ data: { positions: [] } })),
        api.get('/trading/history').catch(() => ({ data: { history: [] } })),
      ]);

      const openTrades   = Array.isArray(posRes.data.positions) ? posRes.data.positions  : [];
      const closedTrades = Array.isArray(histRes.data.history)  ? histRes.data.history   : [];
      setPositions(openTrades);
      setTrades(closedTrades);

      const deals = closedTrades.filter(t => (t.type === 'BUY' || t.type === 'SELL') && t.status === 'closed');
      const wins  = deals.filter(t => (t.profit || 0) > 0);
      const total = deals.reduce((s, t) => s + (t.profit || 0), 0);
      setStats({
        total_trades: deals.length,
        win_trades:   wins.length,
        loss_trades:  deals.length - wins.length,
        total_profit: total,
        win_rate:     deals.length > 0 ? ((wins.length / deals.length) * 100).toFixed(1) : 0,
      });
    } catch {
      toast.error('Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  const fmtDate   = (d) => d ? new Date(d).toLocaleString() : '—';
  const fmtProfit = (p) => {
    const v = parseFloat(p) || 0;
    return `${v >= 0 ? '+' : ''}$${Math.abs(v).toFixed(2)}`;
  };

  const filteredPositions = positions.filter(t =>
    (t.symbol || '').toLowerCase().includes(search.toLowerCase())
  );
  const filteredTrades = trades.filter(t => {
    const matchSearch = (t.symbol || '').toLowerCase().includes(search.toLowerCase());
    const matchType   = typeFilter === 'all'  ||
      (typeFilter === 'buy'  && t.type === 'BUY')  ||
      (typeFilter === 'sell' && t.type === 'SELL') ||
      (typeFilter === 'win'  && (t.profit || 0) > 0) ||
      (typeFilter === 'loss' && (t.profit || 0) < 0);
    return matchSearch && matchType;
  });

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${P}`,
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin .7s linear infinite', margin: '0 auto 12px',
          }}/>
          <p style={{ color: '#475569', fontSize: 14 }}>Loading trades…</p>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 28, maxWidth: 1280, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: -0.5 }}>Collections</h1>
            <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0' }}>All your trading activity</p>
          </div>
          <motion.button whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}
            onClick={loadData}
            style={{
              padding: 10, borderRadius: 10, cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              color: '#64748B', display: 'flex', alignItems: 'center',
            }}>
            <RefreshCw size={16}/>
          </motion.button>
        </motion.div>

        {/* Stats */}
        <div className="th-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total Trades', value: stats.total_trades,                      color: C,  icon: <BarChart2 size={16}/> },
            { label: 'Wins',         value: stats.win_trades,                         color: G,  icon: <Award size={16}/> },
            { label: 'Losses',       value: stats.loss_trades,                        color: R,  icon: <TrendingDown size={16}/> },
            { label: 'Win Rate',     value: `${stats.win_rate}%`,                    color: P2, icon: <Target size={16}/> },
            { label: 'Total P&L',    value: fmtProfit(stats.total_profit),           color: stats.total_profit >= 0 ? G : R, icon: <DollarSign size={16}/> },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              style={{ ...CARD, padding: 18 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8,
                background: `${s.color}14`, color: s.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 10,
              }}>{s.icon}</div>
              <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
              <p style={{ fontSize: 20, fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Win Rate Bar */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ ...CARD, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#CBD5E1' }}>Win Rate</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: G }}>{stats.win_rate}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${stats.win_rate}%` }}
              transition={{ duration: 1, delay: 0.5 }}
              style={{ height: '100%', borderRadius: 8, background: `linear-gradient(90deg,${G},${C})` }}/>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontSize: 11, color: G }}>{stats.win_trades} wins</span>
            <span style={{ fontSize: 11, color: R }}>{stats.loss_trades} losses</span>
          </div>
        </motion.div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { id: 'open',   label: 'Open Positions', count: positions.length },
            { id: 'closed', label: 'Closed Trades',  count: trades.length },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
                background: tab === t.id ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                border: tab === t.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
                color: tab === t.id ? '#000' : '#64748B',
              }}>
              {t.label}
              <span style={{
                padding: '2px 7px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                background: tab === t.id ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.06)',
                color: tab === t.id ? '#000' : '#64748B',
              }}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Search + Type Filter */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by symbol…"
              style={{
                width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10, padding: '10px 14px 10px 36px', color: '#fff', fontSize: 13,
                outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = `${P}50`}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
            />
          </div>
          {tab === 'closed' && (
            <div style={{ display: 'flex', gap: 6 }}>
              {[{ id: 'all', label: 'All' }, { id: 'buy', label: 'Buy' }, { id: 'sell', label: 'Sell' }, { id: 'win', label: 'Wins' }, { id: 'loss', label: 'Losses' }].map(f => (
                <button key={f.id} onClick={() => setTypeFilter(f.id)}
                  style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: typeFilter === f.id ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                    border: typeFilter === f.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
                    color: typeFilter === f.id ? '#000' : '#64748B',
                  }}>{f.label}</button>
              ))}
            </div>
          )}
        </div>

        <AnimatePresence mode="wait">

          {/* Open Positions */}
          {tab === 'open' && (
            <motion.div key="open"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              style={{ ...CARD, overflow: 'hidden' }}>
              {filteredPositions.length === 0 ? (
                <div style={{ padding: '56px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Activity size={26} style={{ color: '#334155' }}/>
                  </div>
                  <p style={{ fontSize: 14, color: '#475569', fontWeight: 600, margin: 0 }}>No open positions</p>
                  <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>Start the bot to begin trading</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                        {['Symbol','Type','Volume','Open Price','Current','Profit','Time'].map(h => (
                          <th key={h} style={{
                            textAlign: 'left', padding: '10px 16px',
                            fontSize: 11, fontWeight: 600, color: '#475569',
                            textTransform: 'uppercase', letterSpacing: 0.5,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPositions.map((pos, i) => {
                        const isBuy = pos.type === 'POSITION_TYPE_BUY';
                        return (
                          <motion.tr key={i}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.03 }}
                            style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 700, color: '#fff' }}>{pos.symbol}</td>
                            <td style={{ padding: '14px 16px' }}><TypeBadge isBuy={isBuy}/></td>
                            <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748B' }}>{pos.volume}</td>
                            <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748B' }}>{pos.openPrice?.toFixed(5)}</td>
                            <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748B' }}>{pos.currentPrice?.toFixed(5)}</td>
                            <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 700, color: (pos.profit || 0) >= 0 ? G : R }}>
                              {fmtProfit(pos.profit)}
                            </td>
                            <td style={{ padding: '14px 16px', fontSize: 11, color: '#475569' }}>{fmtDate(pos.time)}</td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* Closed Trades */}
          {tab === 'closed' && (
            <motion.div key="closed"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              style={{ ...CARD, overflow: 'hidden' }}>
              {filteredTrades.length === 0 ? (
                <div style={{ padding: '56px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BarChart2 size={26} style={{ color: '#334155' }}/>
                  </div>
                  <p style={{ fontSize: 14, color: '#475569', fontWeight: 600, margin: 0 }}>No trades found</p>
                  <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>Completed trades will appear here</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                        {['Symbol','Type','Volume','Open Price','Profit','Status','Opened'].map(h => (
                          <th key={h} style={{
                            textAlign: 'left', padding: '10px 16px',
                            fontSize: 11, fontWeight: 600, color: '#475569',
                            textTransform: 'uppercase', letterSpacing: 0.5,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTrades.map((trade, i) => {
                        const isBuy  = trade.type === 'BUY';
                        const profit = trade.profit || 0;
                        return (
                          <motion.tr key={i}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.02 }}
                            style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 700, color: '#fff' }}>{trade.symbol || '—'}</td>
                            <td style={{ padding: '14px 16px' }}><TypeBadge isBuy={isBuy}/></td>
                            <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748B' }}>{trade.volume || '—'}</td>
                            <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748B' }}>{trade.open_price?.toFixed(5) || '—'}</td>
                            <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 700, color: profit >= 0 ? G : R }}>
                              {fmtProfit(profit)}
                            </td>
                            <td style={{ padding: '14px 16px' }}>
                              <span style={{
                                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5,
                                background: trade.status === 'open' ? `${G}14` : 'rgba(255,255,255,0.04)',
                                color: trade.status === 'open' ? G : '#64748B',
                              }}>
                                {trade.status === 'open' ? 'Open' : 'Closed'}
                              </span>
                            </td>
                            <td style={{ padding: '14px 16px', fontSize: 11, color: '#475569' }}>{fmtDate(trade.opened_at)}</td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      <style>{`
        .th-stats { }
        @media (max-width: 900px) { .th-stats { grid-template-columns: repeat(3,1fr) !important; } }
        @media (max-width: 600px) { .th-stats { grid-template-columns: repeat(2,1fr) !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #334155; }
      `}</style>
    </Layout>
  );
}
